import React, { Component } from 'react';

// Material UI
import { withStyles } from '@material-ui/core/styles';
import {
    FormLabel, Tooltip,
} from '@material-ui/core';

// Bootstrap
import { Tab, Nav } from 'react-bootstrap';

// Components and Others
import PropTypes from 'prop-types';
import TabStyles from 'styles/widgets/tabs/TabsStyles';
import { DealTabsTitle } from 'utils/enum/DealEnum';
import TabContainer from 'components/widgets/tabs/TabContainer';
import FormsTab from 'components/modules/deals/read/form/FormsTab';
import HistoryTab from 'components/modules/deals/read/notes/HistoryTab';
import DealTab from 'components/modules/deals/read/dealTab/DealTab';
import BuyerTab from 'components/modules/deals/read/buyer/BuyerTab';
import VehiclesTab from 'components/modules/deals/read/vehicle/VehiclesTab';
import DealTabsContainer from 'components/containers/deals/read/dealTab/DealTabsContainer';

// GraphQL
import GraphQLClient from 'services/apollo/GraphQLClient';
import DealsQuery from 'services/graphQL/query/DealsQuery';
import DealsMutate from 'services/graphQL/mutate/DealsMutate';
import InventoryQuery from 'services/graphQL/query/InventoryQuery';
import InventoryMutation from 'services/graphQL/mutate/InventoryMutation';

// Utils
import KeyStore from 'utils/KeyStore';
import Permission from 'utils/enum/Permissions';
import CreditDetail from 'components/modules/crm/read/credit/CreditDetail';
import { DOCUMENT_REFERENCE_TYPE } from 'utils/enum/Core';
import { DOCUMENT_TYPES_AVAILABLE_MODULES } from 'utils/enum/modules';
import CustomerQuery from 'services/graphQL/query/CustomerQuery';
import CustomerMutate from 'services/graphQL/mutate/CustomerMutate';
import FileManagerContainer from 'components/widgets/filemanager/FileManagerContainer';
import DocumentTypeMutation from 'services/graphQL/mutate/DocumentTypeMutation';

const styles = (theme) => TabStyles.tabPanel(theme);

class DealTabs extends Component {
    constructor(props) {
        super(props);
        this.keyStore = new KeyStore();
        this.SALES_DEAL_VEHICLE_READ = this.keyStore.hasPermission(Permission.SALES_DEAL_VEHICLE_READ);
        this.SALES_DEAL_CUSTOMER_READ = this.keyStore.hasPermission(Permission.SALES_DEAL_CUSTOMER_READ);
        this.SALES_DEAL_FORMS_READ = this.keyStore.hasPermission(Permission.SALES_DEAL_FORMS_READ);
        this.DEALS_DOCUMENTS_READ = this.keyStore.hasPermission(Permission.DEALS_DOCUMENTS_READ);
        this.DEALS_DOCUMENTS_WRITE = this.keyStore.hasPermission(Permission.DEALS_DOCUMENTS_WRITE);
        this.DEALS_DOCUMENTS_DELETE = this.keyStore.hasPermission(Permission.DEALS_DOCUMENTS_DELETE);
        this.CUSTOMER_DOCUMENTS_READ = this.keyStore.hasPermission(Permission.CUSTOMER_READ);
        this.CUSTOMER_DOCUMENTS_WRITE = this.keyStore.hasPermission(Permission.CUSTOMER_WRITE);
        this.INVENTORY_DOCUMENTS_WRITE = this.keyStore.hasPermission(Permission.INVENTORY_DOCUMENTS_WRITE);
        this.INVENTORY_DOCUMENTS_DELETE = this.keyStore.hasPermission(Permission.INVENTORY_DOCUMENTS_DELETE);
        this.SALES_CREDIT_PULL_READ = this.keyStore.hasPermission(Permission.SALES_CREDIT_PULL_READ);

        this.setTabTitle = this.setTabTitle.bind(this);
        this.graphqlClient = new GraphQLClient();
        this.state = {
            stockNumber: 0,
            stocklotName: null,
            customerCode: null,
        };
    }

    componentDidMount() {
        const { accountNumber, getServicesData, subscribeDealChanged } = this.props;

        getServicesData();
        subscribeDealChanged();

        this.graphqlClient
            .query(DealsQuery.GET_DEAL_VEHICLES, { accountNumber })
            .then((response) => {
                const { data, graphQLErrors } = response;
                if (graphQLErrors) return;

                const vehicle = data?.getDealVehicles?.vehicle;
                if (vehicle && vehicle?.stockNumber) {
                    this.setState({ stockNumber: vehicle?.stockNumber, stocklotName: vehicle?.lotName });
                }
            });

        this.graphqlClient
            .query(DealsQuery.GET_DEAL_CUSTOMER, { accountNumber })
            .then((response) => {
                const { data, graphQLErrors } = response;
                if (graphQLErrors) return;

                const customerCode = data?.getDealCustomer?.buyer?.customerCode;
                if (customerCode) {
                    this.setState({ customerCode });
                }
            });
    }

    componentWillUnmount() {
        const { props: { unsubscribeDealChanged } } = this;
        unsubscribeDealChanged();
    }

    setTabTitle(title, key) {
        const { editingMode } = this.props;
        const asterisk = editingMode.tabName === key ? '*' : '';

        return `${title} ${asterisk}`;
    }

    render() {
        const { stockNumber, customerCode, stocklotName } = this.state;
        const {
            props: {
                classes, accountNumber, onChangeEditingMode, editingMode,
                clientId, setClientId, setLotName, lotName, editingStatus,
                activeTab, setActiveTab,
            },
        } = this;
        const titleDeal = this.setTabTitle('Deal', DealTabsTitle.DEAL);
        const titleBuyer = this.setTabTitle('Buyer', DealTabsTitle.BUYER);
        const titleVehicle = this.setTabTitle('Vehicles', DealTabsTitle.VEHICLE);

        const tabs = [
            {
                title: titleDeal,
                component: <DealTab
                    clientId={clientId}
                    setLotName={setLotName}
                    editingMode={editingMode}
                    setClientId={setClientId}
                    editingStatus={editingStatus}
                    accountNumber={accountNumber}
                    onChangeEditingMode={onChangeEditingMode}
                />,
            },
        ];

        if (this.SALES_DEAL_CUSTOMER_READ) {
            tabs.push({
                title: titleBuyer,
                component: <BuyerTab
                    clientId={clientId}
                    editingMode={editingMode}
                    editingStatus={editingStatus}
                    accountNumber={accountNumber}
                    onChangeEditingMode={onChangeEditingMode}
                />,
            });
        }

        if (this.SALES_DEAL_VEHICLE_READ) {
            tabs.push({
                title: titleVehicle,
                component: <VehiclesTab
                    clientId={clientId}
                    editingMode={editingMode}
                    editingStatus={editingStatus}
                    accountNumber={accountNumber}
                    onChangeEditingMode={onChangeEditingMode}
                />,
            });
        }

        if (this.SALES_DEAL_FORMS_READ) {
            tabs.push({
                title: 'Forms',
                component: <FormsTab
                    accountNumber={accountNumber}
                    lotName={lotName}
                />,
            });
        }

        tabs.push({
            title: 'Credit',
            disabled: !this.SALES_CREDIT_PULL_READ,
            component: <CreditDetail
                recordId={accountNumber}
                recordType="DEAL"
                lotName={lotName}
            />,
        });

        if (this.DEALS_DOCUMENTS_READ) {
            tabs.push({
                title: 'Documents',
                component: <FileManagerContainer
                    documentsProps={[
                        {
                            label: 'Deal Documents',
                            referenceType: DOCUMENT_REFERENCE_TYPE.DEALS,
                            referenceId: accountNumber,
                            referenceName: 'accountNumber',
                            module: DOCUMENT_TYPES_AVAILABLE_MODULES.DEALS,
                            lotName,
                            pullDocumentsQuery: DealsQuery.PULL_AVAILABLE_DEAL_DOCUMENTS,
                            getDocumentUploadSignedURL: DealsQuery.GET_DEAL_DOCUMENT_UPLOAD_SIGNED_URL,
                            createDocumentsMutation: DealsMutate.CREATE_DEAL_DOCUMENTS,
                            editDocumentNameMutation: DealsMutate.EDIT_DEAL_DOCUMENT_NAME,
                            deleteMutation: DealsMutate.DELETE_DEAL_DOCUMENT,
                            canWrite: this.DEALS_DOCUMENTS_WRITE,
                            canDelete: this.DEALS_DOCUMENTS_DELETE,
                            documentTypeMutation: DocumentTypeMutation.UPDATE_FILE_DOCUMENT_TYPE,
                        },
                        {
                            label: 'Customer Documents',
                            referenceType: DOCUMENT_REFERENCE_TYPE.CUSTOMER,
                            referenceId: customerCode,
                            referenceName: 'customerCode',
                            module: DOCUMENT_TYPES_AVAILABLE_MODULES.CUSTOMERS,
                            pullDocumentsQuery: CustomerQuery.PULL_AVAILABLE_CUSTOMER_DOCUMENTS,
                            getDocumentUploadSignedURL: CustomerQuery.GET_CUSTOMER_DOCUMENT_UPLOAD_SIGNED_URL,
                            createDocumentsMutation: CustomerMutate.CREATE_CUSTOMER_DOCUMENTS,
                            editDocumentNameMutation: CustomerMutate.EDIT_CUSTOMER_DOCUMENT_NAME,
                            deleteMutation: CustomerMutate.DELETE_CUSTOMER_DOCUMENT,
                            canWrite: this.CUSTOMER_DOCUMENTS_WRITE,
                            canDelete: this.CUSTOMER_DOCUMENTS_READ,
                            documentTypeMutation: DocumentTypeMutation.UPDATE_FILE_DOCUMENT_TYPE,
                        },
                        {
                            label: 'Sold Vehicles',
                            referenceType: DOCUMENT_REFERENCE_TYPE.INVENTORY,
                            referenceId: stockNumber,
                            referenceName: 'stockNumber',
                            module: DOCUMENT_TYPES_AVAILABLE_MODULES.INVENTORY,
                            lotName: stocklotName,
                            pullDocumentsQuery: InventoryQuery.PULL_AVAILABLE_DOCUMENTS,
                            getDocumentUploadSignedURL: InventoryQuery.GET_DOCUMENT_UPLOAD_SIGNED_URL,
                            createDocumentsMutation: InventoryMutation.CREATE_DOCUMENTS,
                            editDocumentNameMutation: InventoryMutation.EDIT_DOCUMENT_NAME,
                            deleteMutation: InventoryMutation.DELETE_DOCUMENT,
                            canWrite: this.INVENTORY_DOCUMENTS_WRITE,
                            canDelete: this.INVENTORY_DOCUMENTS_DELETE,
                            documentTypeMutation: DocumentTypeMutation.UPDATE_FILE_DOCUMENT_TYPE,
                        },
                    ]}
                />,
            });
        }

        tabs.push({
            title: 'History',
            component: <HistoryTab
                dealId={accountNumber}
            />,
        });

        return (
            <TabContainer className={classes.tabDetail}>
                { editingStatus?.isEditing && editingStatus.editingById !== this.keyStore.getUserId() && (
                    <div className={`${classes.headerBanner} ${classes.yellowBanner}`}>
                        <FormLabel>{`This deal is being edited by ${editingStatus.editingBy}`}</FormLabel>
                    </div>
                )}
                <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(Number(k))}>
                    <Nav variant="tabs" className={`${classes.navbar} ${classes.borderContainer}`}>
                        {tabs.map((item, index) => {
                            const { disabled } = item;

                            return (
                                <Tooltip
                                    key={index}
                                    title={disabled ? 'Insufficient permissions. Please contact the HR department for access' : ''}
                                    disableHoverListener={!disabled}
                                    placement="bottom-start"
                                >
                                    <span>
                                        <Nav.Item>
                                            <Nav.Link
                                                eventKey={index}
                                                disabled={disabled}
                                                className={classes.tab}
                                            >
                                                {item.title}
                                            </Nav.Link>
                                        </Nav.Item>
                                    </span>
                                </Tooltip>
                            );
                        })}
                    </Nav>

                    <Tab.Content>
                        {tabs.map((item, index) => (
                            <Tab.Pane className={classes.tabPane} eventKey={index} key={index}>
                                {item.component}
                            </Tab.Pane>
                        ))}
                    </Tab.Content>
                </Tab.Container>
            </TabContainer>
        );
    }
}

DealTabs.propTypes = {
    activeTab: PropTypes.number.isRequired,
    setActiveTab: PropTypes.func.isRequired,
    accountNumber: PropTypes.number.isRequired,
    editingMode: PropTypes.shape({
        isEditing: PropTypes.bool,
        sectionName: PropTypes.string,
        tabName: PropTypes.string,
    }).isRequired,
    editingStatus: PropTypes.shape({
        isEditing: PropTypes.bool,
        editingById: PropTypes.number,
        editingBy: PropTypes.string,
    }).isRequired,
    lotName: PropTypes.string,
    onChangeEditingMode: PropTypes.func.isRequired,
    classes: PropTypes.oneOfType([PropTypes.object]).isRequired,
    clientId: PropTypes.string.isRequired,
    setClientId: PropTypes.func.isRequired,
    setLotName: PropTypes.func.isRequired,
    getServicesData: PropTypes.func.isRequired,
    subscribeDealChanged: PropTypes.func.isRequired,
    unsubscribeDealChanged: PropTypes.func.isRequired,
};

DealTabs.defaultProps = {
    lotName: null,
};

export default withStyles(styles)(DealTabsContainer(DealTabs));
